import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

// Mirrors this app's Contract §5.1 button spec (see components/common/Button.js)
// so shadcn-built pieces stay visually identical to the rest of the app.
const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 rounded-lg font-semibold whitespace-nowrap ' +
    'transition-[background-color,border-color,color,transform] duration-150 active:translate-y-px ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
    'disabled:opacity-50 disabled:pointer-events-none',
    {
        variants: {
            variant: {
                primary: 'bg-accent text-white shadow-[0_1px_2px_rgb(var(--c-shadow)/0.12),inset_0_1px_0_rgb(255_255_255/0.12)] hover:bg-accent-dark active:bg-accent-dark',
                secondary: 'bg-card text-text-primary border border-border shadow-sm hover:bg-surface2 hover:border-text-muted/50',
                ghost: 'text-text-secondary hover:bg-surface2 hover:text-text-primary',
                danger: 'bg-card text-danger border border-border hover:bg-danger/10 hover:border-danger/40',
                destructive: 'bg-danger text-white shadow-[0_1px_2px_rgb(var(--c-shadow)/0.12),inset_0_1px_0_rgb(255_255_255/0.12)] hover:bg-danger/90',
                outline: 'bg-transparent text-text-primary border border-border hover:bg-surface2',
            },
            size: {
                sm: 'h-8 px-3 text-xs',
                default: 'h-10 px-4 text-sm',
                lg: 'h-11 px-5 text-base',
                xl: 'h-14 px-6 text-lg',
            },
        },
        defaultVariants: { variant: 'primary', size: 'default' },
    }
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
Button.displayName = 'Button';

export { Button, buttonVariants };
